import { JSX } from 'react'

import { Accordion, NavLink } from '@mantine/core'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Indicator } from '@mantine/core'

import Settings from 'src/Assets/settings.svg?react'
import Wheel from 'src/Assets/wheel.svg?react'
import Document from 'src/Assets/briefcase.svg?react'
import Client from 'src/Assets/users.svg?react'
import Offer from 'src/Assets/document.svg?react'
import Certificate from 'src/Assets/certificate.svg?react'
import Equipment from 'src/Assets/equipment.svg?react'
import SettingsAdd from 'src/Assets/settings-plus.svg?react'
import Adjustments from 'src/Assets/adjustments-horizontal.svg?react'
import Profile from 'src/Assets/profile.svg?react'
import { RoutesEnum } from 'src/Router/types.ts'
import { colors, device } from 'src/Helpers/constants.ts'
import { Role, useGetProfileQuery } from 'src/Redux/api/authApi.ts'
import {
  useGetInboxDocumentsQuery,
  ApprovalStatusEnum,
} from 'src/Redux/api/documentsApi.ts'

interface MenuItem {
  icon: JSX.Element
  value: string
  isAdmin?: boolean
  subitems: Array<{
    name: string
    route: RoutesEnum
    icon: JSX.Element
  }>
}

const getMenuItems = (hasInboxDocuments: boolean): MenuItem[] => [
  {
    icon: <Document width={25} height={25} color={colors.thirdGray} />,
    value: 'Продажи',
    subitems: [
      {
        name: 'Клиенты',
        route: RoutesEnum.CLIENTS,
        icon: <Client width={25} height={25} color={colors.thirdGray} />,
      },
      {
        name: 'Коммерческие предложения',
        route: RoutesEnum.OFFERS,
        icon: <Offer width={25} height={25} color={colors.thirdGray} />,
      },
      {
        name: 'Договоры',
        route: RoutesEnum.CONTRACTS,
        icon: <Certificate width={25} height={25} color={colors.thirdGray} />,
      },
    ],
  },
  {
    icon: <Wheel width={25} height={25} color={colors.thirdGray} />,
    value: 'Транспорт',
    subitems: [
      {
        name: 'Транспортные средства и оборудование',
        route: RoutesEnum.TRANSPORTS,
        icon: <Equipment width={25} height={25} color={colors.thirdGray} />,
      },
      {
        name: 'Навесное оборудование',
        route: RoutesEnum.TOOLS,
        icon: <SettingsAdd width={25} height={25} color={colors.thirdGray} />,
      },
    ],
  },
  {
    isAdmin: true,
    icon: <Settings width={25} height={25} color={colors.thirdGray} />,
    value: 'НСИ и Администрирование',
    subitems: [
      {
        name: 'Настройки КП',
        route: RoutesEnum.SETTINGS,
        icon: <Adjustments width={25} height={25} color={colors.thirdGray} />,
      },
      {
        name: 'Настройки типов техники',
        route: RoutesEnum.TRANSPORT_SETTINGS,
        icon: <Adjustments width={25} height={25} color={colors.thirdGray} />,
      },
      {
        name: 'Пользователи',
        route: RoutesEnum.USERS,
        icon: <Client width={25} height={25} color={colors.thirdGray} />,
      },
    ],
  },
  {
    icon: hasInboxDocuments ? (
      <Indicator color='pink' withBorder processing>
        <Profile width={25} height={25} color={colors.thirdGray} />
      </Indicator>
    ) : (
      <Profile width={25} height={25} color={colors.thirdGray} />
    ),
    value: 'Документы',
    subitems: [
      {
        name: 'Входящие',
        route: RoutesEnum.INBOX,
        icon: hasInboxDocuments ? (
          <Indicator color='pink' withBorder processing>
            <Client width={25} height={25} color={colors.thirdGray} />
          </Indicator>
        ) : (
          <Client width={25} height={25} color={colors.thirdGray} />
        ),
      },
    ],
  },
]

const StyledAccordionItem = styled(Accordion.Item)`
  border: none;
  padding: 10px 0;
  button {
    padding: 0;
    &:focus {
      outline: none;
    }
  }
`

const StyledAccordionControl = styled(Accordion.Control)`
  .mantine-Accordion-label {
    padding: 0;
  }

  margin-bottom: 5px;
  font-size: 18px;
  font-weight: 600;
  span {
    font-size: 16px;
    font-weight: 600;
  }

  .mantine-Accordion-label {
    font-size: 14px !important;
    color: ${colors.thirdGray};
  }

  @media ${device.tabletMax} {
    .mantine-Accordion-label {
      font-size: 13px !important;
    }
  }
`

const StyledPanel = styled(Accordion.Panel)<{
  $isCollapsed?: boolean
}>`
  .mantine-Accordion-content {
    padding: 0;
    color: ${colors.thirdBlue};
  }

  a {
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '8px' : '0')};
  }
`

interface UseMenu {
  itemsList: JSX.Element[] | JSX.Element
  defaultValue: string | null
}

export const useMenu = (opened: boolean): UseMenu => {
  const { data: user } = useGetProfileQuery()
  const { data: inboxData } = useGetInboxDocumentsQuery(
    {
      limit: 1,
      status: ApprovalStatusEnum.IN_PROCESS,
    },
    {
      pollingInterval: 120000, // опрос каждые 2 минуты
      refetchOnFocus: true, // обновление при фокусе окна
      refetchOnReconnect: true, // обновление при восстановлении соединения
    },
  )
  const isAdmin = user?.role === Role.ADMIN

  const hasInboxDocuments = (inboxData?.documents?.length ?? 0) > 0
  const menuItems = getMenuItems(hasInboxDocuments)

  const activeMenuGroup = menuItems.find((item) => {
    if (item.subitems) {
      return item.subitems.find((subitem) => {
        return window.location.pathname === subitem.route
      })
    }
    return false
  })

  const itemsList = menuItems
    .filter((item) => {
      if (item.isAdmin) {
        return isAdmin
      }
      return true
    })
    .map((item) => {
      return (
        <StyledAccordionItem key={item.value} value={item.value}>
          <StyledAccordionControl icon={item.icon}>
            {opened ? item.value.toUpperCase() : null}
          </StyledAccordionControl>
          <StyledPanel $isCollapsed={opened}>
            {item.subitems.map((subitem) => {
              const isActive = window.location.pathname === subitem.route

              return (
                <NavLink
                  key={subitem.name}
                  leftSection={subitem.icon}
                  label={opened ? subitem.name : null}
                  component={Link}
                  to={subitem.route}
                  style={{
                    fontSize: '16px',
                    fontWeight: '400',
                    color: isActive ? colors.fourthBlue : colors.thirdGray,
                  }}
                />
              )
            })}
          </StyledPanel>
        </StyledAccordionItem>
      )
    })

  const defaultValue = activeMenuGroup?.value || null

  return {
    itemsList,
    defaultValue,
  }
}
